import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AVATAR_WIDTH } from '../../../../../constants';
import { TranslationModule } from '../../../../../i18n/translation-module';
import { ActivityInterface, GetMultiCrmObjectResponseInterface } from '@vendasta/crm';
import { CommonModule } from '@angular/common';
import { Component, computed, Input, Signal, signal, WritableSignal } from '@angular/core';
import {
  CrmFieldService,
  StandardIds,
  SystemFieldIds,
  CrmObjectDisplayService,
  CrmObjectService,
} from '../../../../../shared-services';
import { MatCardModule } from '@angular/material/card';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ObjectType } from '../../../../../tokens-and-interfaces';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivityCardComponent } from './activity-card/activity-card.component';
import { CollapsibleSectionComponent } from '../../../../collapsible-section/collapsible-section.component';
import { SanitizeHtmlPipe } from '../../../activities/sanitize-html.pipe';
import { getActivityUrl } from '../../../activities/activity-url-builder';
import { CommunicationType, CommunicationIcons } from '../../../activities/activity.interface';
import { RouterLink } from '@angular/router';
import { ActivityAssociationService } from '../../../activities/activity-association.service';

@Component({
  selector: 'crm-activity-communication',
  templateUrl: './communication.component.html',
  styleUrls: ['./communication.component.scss'],
  providers: [ActivityAssociationService],
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    MatCardModule,
    GalaxyAvatarModule,
    GalaxyPipesModule,
    SanitizeHtmlPipe,
    ActivityCardComponent,
    CollapsibleSectionComponent,
    RouterLink,
  ],
})
export class CrmCommunicationComponent {
  private _activity: WritableSignal<ActivityInterface> = signal({});
  private _objectType: WritableSignal<ObjectType> = signal('Contact');
  private _associatedObjectId: WritableSignal<string> = signal('');

  @Input() set activity(activity: ActivityInterface) {
    this._activity.set(activity);
    this.associationService.activity = activity;
  }

  get activity() {
    return this._activity();
  }

  get activitySignal(): Signal<ActivityInterface> {
    return this._activity.asReadonly();
  }

  @Input() set objectType(objectType: ObjectType) {
    this._objectType.set(objectType);
  }

  @Input() set associatedObjectId(crmObjectId: string) {
    this._associatedObjectId.set(crmObjectId);
  }

  @Input() showAvatar = true;

  protected readonly AVATAR_WIDTH = AVATAR_WIDTH;
  protected readonly dateFormat = DateFormat.medium;

  showPrimaryAssociation = computed(() => {
    if (!this._associatedObjectId()) return false;
    if (this.isSMSRequest()) return false;
    return !this.isInbox();
  });
  primaryAssociation = this.associationService.primaryAssociation;
  routePrefix = this.associationService.routePrefix;
  primaryAssociationLink = computed(() => {
    if (!this.primaryAssociation()) return '';
    const linkUrl = this.primaryAssociation()?.linkUrl;
    return `${this.routePrefix()}/${linkUrl}`;
  });

  associatedCrmObjectName$ = toObservable(this._activity).pipe(switchMap(() => this.getAssociatedObjectName()));
  associatedCrmObjectName = toSignal(this.associatedCrmObjectName$);

  constructor(
    private readonly fieldService: CrmFieldService,
    private readonly translateService: TranslateService,
    private readonly crmObjectService: CrmObjectService,
    private readonly objectDisplayService: CrmObjectDisplayService,
    private readonly associationService: ActivityAssociationService,
  ) {}

  cardInfo: Signal<ActivityCardInfo> = computed(() => {
    return {
      activityName: this.getStringFieldData(this._activity(), StandardIds.ActivityName) ?? '',
      sourceName: this.getStringFieldData(this._activity(), StandardIds.ActivitySourceName),
      body: this.getStringFieldData(this._activity(), StandardIds.ActivityCommunicationBody),
      effectiveDate: this.fieldService.getFieldValueFromCrmObject(
        this._activity(),
        SystemFieldIds.ActivityEffectiveDate,
      )?.dateValue,
      activityUrl: getActivityUrl(this.getStringFieldData(this._activity(), StandardIds.ActivityUrl)),
      activityUrlName: this.getStringFieldData(this._activity(), StandardIds.ActivityUrlName) ?? '',
      communicationType: this.getStringFieldData(this._activity(), StandardIds.ActivityCommunicationType) ?? '',
      communicationStatus: this.getStringFieldData(this._activity(), StandardIds.ActivityCommunicationStatus) ?? '',
      communicationChannel: this.getStringFieldData(this._activity(), StandardIds.ActivityCommunicationChannel) ?? '',
    };
  });

  activityIcon = computed(() => {
    const info = this.cardInfo();
    if (info.communicationType === CommunicationType.PhoneCall) {
      return CommunicationIcons[CommunicationType.PhoneCall];
    }
    if (info.communicationType === CommunicationType.CampaignSMS) {
      return CommunicationIcons[CommunicationType.CampaignSMS];
    }
    if (info.communicationChannel === CommunicationType.SMS) {
      return CommunicationIcons[CommunicationType.SMS];
    }
    if (info.communicationChannel === CommunicationType.Inbox) {
      return CommunicationIcons[CommunicationType.Inbox];
    }
    return CommunicationIcons.default;
  });

  headerText = computed(() => {
    const { communicationType, communicationStatus } = this.cardInfo();
    if (communicationType !== CommunicationType.CampaignSMS) {
      return '';
    }
    const baseText = this.translateService.instant('ACTIVITY_TYPES.COMMUNICATION_SUBTYPES.CAMPAIGN_SMS') + ' ';
    const verbText = this.getStatusText(communicationStatus);
    return `${baseText}${verbText}`.trim();
  });

  private getStatusText(status: string): string {
    switch (status) {
      case 'sent':
        return this.translateService.instant('ACTIVITY.SMS_TITLE.SENT') + ' ';
      case 'delivered':
        return this.translateService.instant('ACTIVITY.SMS_TITLE.DELIVERED') + ' ';
      case 'failed':
        return this.translateService.instant('ACTIVITY.SMS_TITLE.FAILED') + ' ';
      case 'undelivered':
        return this.translateService.instant('ACTIVITY.SMS_TITLE.UNDELIVERED') + ' ';
      default:
        return '';
    }
  }

  isCampaignSMS = computed(() => {
    return this.cardInfo().communicationType === CommunicationType.CampaignSMS;
  });

  isSMSRequest = computed(() => {
    return this.cardInfo().communicationChannel === CommunicationType.SMS;
  });

  isInbox = computed(() => {
    return this.cardInfo().communicationChannel === CommunicationType.Inbox;
  });

  getAssociatedObjectName(): Observable<string> {
    const associatedContactID = this.activity?.associations?.find(
      (association) => association?.crmObjectType === 'Contact',
    )?.crmObjectId;

    if (associatedContactID) {
      return this.getObjectName(associatedContactID, 'Contact');
    } else {
      return this.useAssociatedObjectID();
    }
  }

  useAssociatedObjectID(): Observable<string> {
    if (this._objectType() === 'Contact') {
      return this.getObjectName(this._associatedObjectId(), 'Contact');
    }
    if (this._objectType() === 'Company') {
      return this.getObjectName(this._associatedObjectId(), 'Company');
    }
    return of('');
  }

  private getObjectName(objectID: string, objectName: ObjectType): Observable<string> {
    return this.crmObjectService.getMultiObject(objectName, [objectID]).pipe(
      map((data: GetMultiCrmObjectResponseInterface) => {
        const crmObject = data?.crmObjects?.[0];
        if (!crmObject) {
          return '';
        }
        return this.objectDisplayService.getObjectDisplayName(objectName, crmObject);
      }),
    );
  }

  private getStringFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }
}

interface ActivityCardInfo {
  activityName: string;
  sourceName: string;
  body: string;
  effectiveDate: Date | undefined;
  activityUrl: string | null;
  activityUrlName: string;
  communicationType: string;
  communicationChannel: string;
  communicationStatus: string;
}
